import {css} from '../utils.js';

const styles = css`
    body {
        /* Global Variables */

        /* Colors */
        --ddk-color-accent1-accent105: #f7f8fb;
        --ddk-color-accent1-accent110: #ecf0f7;
        --ddk-color-accent1-accent120: #d2ddec;
        --ddk-color-accent1-accent130: #afc2dd;
        --ddk-color-accent1-accent140: #8ea8cc;
        --ddk-color-accent1-accent150: #6582ac;
        --ddk-color-accent1-accent160: #486186;
        --ddk-color-accent1-accent170: #344762;
        --ddk-color-accent1-accent180: #253247;
        --ddk-color-accent1-accent190: #18202d;
        --ddk-color-accent2-accent205: #f7f8fb;
        --ddk-color-accent2-accent210: #ecf0f7;
        --ddk-color-accent2-accent220: #d2ddec;
        --ddk-color-accent2-accent230: #afc2dd;
        --ddk-color-accent2-accent240: #8ea8cc;
        --ddk-color-accent2-accent250: #6582ac;
        --ddk-color-accent2-accent260: #486186;
        --ddk-color-accent2-accent270: #344762;
        --ddk-color-accent2-accent280: #253247;
        --ddk-color-accent2-accent290: #18202d;
        --ddk-color-base-base00: #ffffff;
        --ddk-color-base-base05: #f9f9f9;
        --ddk-color-base-base10: #f1f1f1;
        --ddk-color-base-base20: #dddddd;
        --ddk-color-base-base30: #c1c1c1;
        --ddk-color-base-base40: #a6a6a6;
        --ddk-color-base-base50: #818181;
        --ddk-color-base-base60: #606060;
        --ddk-color-base-base70: #464646;
        --ddk-color-base-base80: #323232;
        --ddk-color-base-base90: #202020;
        --ddk-color-base-base100: #101010;
        --ddk-color-interactive-interactive05: #fef8fa;
        --ddk-color-interactive-interactive10: #fdecf0;
        --ddk-color-interactive-interactive20: #fcd2da;
        --ddk-color-interactive-interactive30: #faabbb;
        --ddk-color-interactive-interactive40: #f88299;
        --ddk-color-interactive-interactive50: #ee395c;
        --ddk-color-interactive-interactive60: #c3001d;
        --ddk-color-interactive-interactive70: #91000d;
        --ddk-color-interactive-interactive80: #6b0008;
        --ddk-color-interactive-interactive90: #490005;
        --ddk-color-negative-negative05: #fff7ee;
        --ddk-color-negative-negative10: #ffefda;
        --ddk-color-negative-negative20: #ffd69c;
        --ddk-color-negative-negative30: #ffb148;
        --ddk-color-negative-negative40: #fd8702;
        --ddk-color-negative-negative50: #cc6400;
        --ddk-color-negative-negative60: #9a4900;
        --ddk-color-negative-negative70: #723500;
        --ddk-color-negative-negative80: #522600;
        --ddk-color-negative-negative90: #341900;
        --ddk-color-positive-positive05: #f5f9f4;
        --ddk-color-positive-positive10: #ebf2ea;
        --ddk-color-positive-positive20: #cfe2cc;
        --ddk-color-positive-positive30: #a8caa1;
        --ddk-color-positive-positive40: #82b279;
        --ddk-color-positive-positive50: #4e9041;
        --ddk-color-positive-positive60: #246e15;
        --ddk-color-positive-positive70: #0f5102;
        --ddk-color-positive-positive80: #073b00;
        --ddk-color-positive-positive90: #032600;

        /* Spacing */

        --ddk-grid-none: 0px;
        --ddk-grid-quarter: 2px;
        --ddk-grid-half: 4px;
        --ddk-grid-1: 8px;
        --ddk-grid-1-half: 12px;
        --ddk-grid-2: 16px;
        --ddk-grid-3: 24px;
        --ddk-grid-4: 32px;
        --ddk-grid-5: 40px;
        --ddk-grid-6: 48px;
        --ddk-grid-7: 52px;
        --ddk-grid-8: 64px;
        --ddk-grid-9: 72px;
        --ddk-grid-10: 80px;
        --ddk-grid-11: 88px;
        --ddk-grid-12: 96px;
        --ddk-grid-13: 104px;
        --ddk-grid-14: 112px;
        --ddk-grid-15: 120px;
        --ddk-grid-16: 128px;

        --ddk-radius-none: var(--ddk-grid-none) --ddk-radius-xs:
            var(--ddk-grid-quarter);
        --ddk-radius-sm: var(--ddk-grid-half);
        --ddk-radius-md: var(--ddk-grid-1);
        --ddk-radius-lg: var(--ddk-grid-4);
        --ddk-radius-xl: var(--ddk-grid-6);

        /* Typography */

        /* Fonts */
        --ddk-font-size-sm: 14px;
        --ddk-font-size-md: 16px;
        --ddk-font-size-lg: 24px;
        --ddk-font-size-xl: 32px;
        --ddk-font-size-2xl: 48px;
        --ddk-font-size-3xl: 64px;

        --ddk-font-light: 300;
        --ddk-font-semi-light: 400;
        --ddk-font-normal: 500;
        --ddk-font-semi-bold: 600;
        --ddk-font-bold: 800;

        /* Tokens */

        /* Light Mode */

        /* Colors */
        --ddk-border-accent-primary: var(--ddk-color-accent1-accent170);
        --ddk-border-accent-secondary: var(--ddk-color-accent1-accent140);
        --ddk-border-accent-tertiary: var(--ddk-color-accent1-accent130);
        --ddk-border-base-inverse-primary: #ffffff;
        --ddk-border-base-inverse-secondary: #ffffff;
        --ddk-border-base-inverse-tertiary: #ffffff;
        --ddk-border-base-primary: var(--ddk-color-base-base40);
        --ddk-border-base-secondary: var(--ddk-color-base-base30);
        --ddk-border-base-tertiary: var(--ddk-color-base-base20);
        --ddk-border-interactive-primary: var(
            --ddk-color-interactive-interactive70
        );
        --ddk-border-interactive-secondary: var(
            --ddk-color-interactive-interactive40
        );
        --ddk-border-interactive-tertiary: var(
            --ddk-color-interactive-interactive30
        );
        --ddk-border-negative-primary: #ffffff;
        --ddk-border-positive-primary: var(--ddk-color-positive-positive70);
        --ddk-border-positive-primary-inverse: var(
            --ddk-color-positive-positive20
        );

        --ddk-icon-accent-inverse-primary: var(--ddk-color-accent1-accent120);
        --ddk-icon-accent-primary: var(--ddk-color-accent1-accent170);
        --ddk-icon-base-inverse-primary: var(--ddk-color-base-base00);
        --ddk-icon-base-inverse-secondary: var(--ddk-color-base-base20);
        --ddk-icon-base-inverse-tertiary: #ffffff;
        --ddk-icon-base-primary: var(--ddk-color-base-base70);
        --ddk-icon-base-secondary: var(--ddk-color-base-base50);
        --ddk-icon-base-tertiary: #ffffff;
        --ddk-icon-interactive-inverse-primary: var(--ddk-color-base-base00);
        --ddk-icon-interactive-inverse-secondary: var(
            --ddk-color-interactive-interactive20
        );
        --ddk-icon-interactive-inverse-tertiary: #ffffff;
        --ddk-icon-interactive-primary: var(
            --ddk-color-interactive-interactive70
        );
        --ddk-icon-interactive-secondary: #ffffff;
        --ddk-icon-interactive-tertiary: #ffffff;
        --ddk-icon-negative-inverse-primary: var(
            --ddk-color-negative-negative20
        );
        --ddk-icon-negative-primary: var(--ddk-color-negative-negative70);
        --ddk-icon-positive-inverse-primary: var(
            --ddk-color-positive-positive20
        );
        --ddk-icon-positive-primary: var(--ddk-color-positive-positive70);

        --ddk-surface-accent-inverse-surface1: var(
            --ddk-color-accent1-accent110
        );
        --ddk-surface-accent-inverse-surface2: var(
            --ddk-color-accent1-accent120
        );
        --ddk-surface-accent-inverse-surface3: var(
            --ddk-color-accent1-accent130
        );
        --ddk-surface-accent-inverse-surface4: var(
            --ddk-color-accent1-accent140
        );

        --ddk-surface-accent-surface1: var(--ddk-color-accent1-accent190);
        --ddk-surface-accent-surface2: var(--ddk-color-accent1-accent180);
        --ddk-surface-accent-surface3: var(--ddk-color-accent1-accent170);
        --ddk-surface-accent-surface4: var(--ddk-color-accent1-accent160);
        --ddk-surface-base-inverse-surface1: var(--ddk-color-base-base90);
        --ddk-surface-base-inverse-surface2: var(--ddk-color-base-base80);
        --ddk-surface-base-inverse-surface3: var(--ddk-color-base-base70);
        --ddk-surface-base-inverse-surface4: var(--ddk-color-base-base60);

        --ddk-surface-base-surface1: var(--ddk-color-base-base00);
        --ddk-surface-base-surface2: var(--ddk-color-base-base10);
        --ddk-surface-base-surface3: var(--ddk-color-base-base20);
        --ddk-surface-base-surface4: var(--ddk-color-base-base30);

        --ddk-surface-interactive-inverse-surface1: var(
            --ddk-color-interactive-interactive10
        );
        --ddk-surface-interactive-inverse-surface2: var(
            --ddk-color-interactive-interactive20
        );
        --ddk-surface-interactive-inverse-surface3: var(
            --ddk-color-interactive-interactive30
        );
        --ddk-surface-interactive-inverse-surface4: var(
            --ddk-color-interactive-interactive40
        );

        --ddk-surface-interactive-surface1: var(
            --ddk-color-interactive-interactive90
        );
        --ddk-surface-interactive-surface2: var(
            --ddk-color-interactive-interactive80
        );
        --ddk-surface-interactive-surface3: var(
            --ddk-color-interactive-interactive70
        );
        --ddk-surface-interactive-surface4: var(
            --ddk-color-interactive-interactive60
        );

        --ddk-surface-negative-surface1: var(--ddk-color-negative-negative70);
        --ddk-surface-negative-inverse-surface1: var(
            --ddk-color-negative-negative20
        );
        --ddk-surface-positive-inverse-surface1: var(
            --ddk-color-positive-positive20
        );
        --ddk-surface-positive-surface1: var(--ddk-color-positive-positive70);

        --ddk-text-accent-inverse-primary: var(--ddk-color-accent1-accent120);
        --ddk-text-accent-primary: var(--ddk-color-accent1-accent170);

        --ddk-text-base-inverse-primary: var(--ddk-color-base-base00);
        --ddk-text-base-inverse-secondary: var(--ddk-color-base-base20);
        --ddk-text-base-inverse-tertiary: var(--ddk-color-base-base30);
        --ddk-text-base-primary: var(--ddk-color-base-base100);
        --ddk-text-base-secondary: var(--ddk-color-base-base70);
        --ddk-text-base-tertiary: var(--ddk-color-base-base50);

        --ddk-text-interactive-inverse-primary: var(
            --ddk-color-interactive-interactive20
        );
        --ddk-text-interactive-inverse-secondary: #ffffff;
        --ddk-text-interactive-inverse-tertiary: #ffffff;
        --ddk-text-interactive-primary: var(
            --ddk-color-interactive-interactive70
        );
        --ddk-text-interactive-secondary: #ffffff;
        --ddk-text-interactive-tertiary: #ffffff;

        --ddk-text-negative-inverse-primary: var(
            --ddk-color-negative-negative20
        );
        --ddk-text-negative-primary: var(--ddk-color-negative-negative70);
        --ddk-text-positive-inverse-primary: var(
            --ddk-color-positive-positive20
        );
        --ddk-text-positive-primary: var(--ddk-color-positive-positive70);

        /* Spacing */
        --ddk-padding-none: var(--ddk-grid-none);
        --ddk-padding-2xs: var(--ddk-grid-half);
        --ddk-padding-xs: var(--ddk-grid-1);
        --ddk-padding-sm: var(--ddk-grid-2);
        --ddk-padding-md: var(--ddk-grid-3);
        --ddk-padding-lg: var(--ddk-grid-5);
        --ddk-padding-xl: var(--ddk-grid-6);
    }
`;

export default styles;
